import { Injectable } from '@angular/core';
import { BhtpEditService } from '@bhtp-client-api/edit';
import {
    BhtpStateService, BhtpRulesService, BhtpEligibilityService,
    BhtpCountryService, BhtpJurisdictionsService
} from '@bhtp-client-api/eligibility';
import { BhtpPurchaseService, BhtpPackageService, BhtpLicenseService } from '@bhtp-client-api/purchase';
import {
    BhtpAirlineTypeaheadService, BhtpCarRentalTypeaheadService, BhtpCountryTypeaheadService,
    BhtpCruiseTypeaheadService, BhtpHotelTypeaheadService,
    BhtpRentalCompanyTypeaheadService, BhtpTourTypeaheadService, TypeaheadConfig
} from '@bhtp-client-api/typeahead';
import {
    PaymentService, CustomerService, Features, FeatureService, AgentNameTypeaheadService,
    AgentCodeTypeaheadService,  TramsService
} from './../shared';
import { SaveQuoteService } from '../quotes/save-quote.service';
import { EmailQuoteService } from '../email-quote/email-quote.service';
import { PolicyDocumentService } from '../policy/policy-document.service';

// Stops typescript errors from grabbing things from the window
interface IBhtpWindow extends Window {
    global_client_base_api_url: any;
    global_base_eligibility_url: any;
    global_client_base_eligibility_url: any;
    basePaymentUrl: any;
    global_airCareRatingIds: string;
    global_default_flight_segments: number;
    global_consumer_domain: string;
    global_agentsGatewayApi_url: string;
}
declare var window: IBhtpWindow; // Stops typescript errors from grabbing things from the window

@Injectable()
export class ServiceSetup {

    public injectUrls(): void {
        let baseClientsApiUrl: string = window.global_client_base_api_url;
        let baseEligibilityUrl: string = window.global_base_eligibility_url;
        let baseClientsEligibilityUrl: string = window.global_client_base_eligibility_url;
        let basePaymentUrl: string = window.basePaymentUrl;
        let baseAgentsGatewayUrl: string = window.global_agentsGatewayApi_url;
        // Bhtp Client Api Requests
        this.policyEditService.setBaseUrl(baseClientsApiUrl);
        this.purchaseService.setBaseUrl(baseClientsApiUrl);
        this.licenseService.setBaseUrl(baseClientsApiUrl);
        this.customerService.setBaseUrl(baseClientsApiUrl);
        this.packageService.setBaseUrl(baseClientsApiUrl);
        this.agentNameTypeaheadService.setBaseUrl(baseClientsApiUrl);
        this.agentCodeTypeaheadService.setBaseUrl(baseClientsApiUrl);

        // Bhtp Clients Api Eligibility Requests
        this.rulesService.setBaseUrl(baseClientsEligibilityUrl);

        // jurisdictions
        this.jurisdictionsService.setBaseUrl(baseClientsEligibilityUrl);

        // Bhtp Api Eligibility Requests
        this.stateService.setBaseUrl(baseEligibilityUrl);
        this.countryService.setBaseUrl(baseEligibilityUrl);
        this.airlineTypeaheadService.setBaseUrl(baseEligibilityUrl);
        this.carRentalTypeaheadService.setBaseUrl(baseEligibilityUrl);
        this.countryTypeaheadService.setBaseUrl(baseEligibilityUrl);
        this.cruiseTypeaheadService.setBaseUrl(baseEligibilityUrl);
        this.hotelTypeaheadService.setBaseUrl(baseEligibilityUrl);
        this.rentalCompTypeaheadService.setBaseUrl(baseEligibilityUrl);
        this.tourTypeaheadService.setBaseUrl(baseEligibilityUrl);
        this.eligibilityService.setBaseUrl(baseEligibilityUrl);


        // Payment Api
        this.paymentService.setBaseUrl(basePaymentUrl);

        // Agents Gateway
        this.saveQuoteService.setBaseUrl(baseAgentsGatewayUrl);
        this.emailQuoteService.setBaseUrl(baseAgentsGatewayUrl);
        this.policyDocumentService.setBaseUrl(baseAgentsGatewayUrl);
    }

    public injectOrigin(): void {
        let origin: string = 'agent';

        this.stateService.setOrigin(origin);
        this.countryService.setOrigin(origin);
        this.policyEditService.setOrigin(origin);
        this.rulesService.setOrigin(origin);
        this.purchaseService.setOrigin(origin);
        this.airlineTypeaheadService.setOrigin(origin);
        this.carRentalTypeaheadService.setOrigin(origin);
        this.countryTypeaheadService.setOrigin(origin);
        this.cruiseTypeaheadService.setOrigin(origin);
        this.hotelTypeaheadService.setOrigin(origin);
        this.rentalCompTypeaheadService.setOrigin(origin);
        this.tourTypeaheadService.setOrigin(origin);
        this.eligibilityService.setOrigin(origin);
        this.jurisdictionsService.setOrigin(origin);
        this.licenseService.setOrigin(origin);
        this.customerService.setOrigin(origin);
        this.packageService.setOrigin(origin);
        this.agentNameTypeaheadService.setOrigin(origin);
        this.agentCodeTypeaheadService.setOrigin(origin);
        this.saveQuoteService.setOrigin(origin);
        this.emailQuoteService.setOrigin(origin);
        this.policyDocumentService.setOrigin(origin);
    }

    public injectAuthToken(token: string): void {
        this.policyEditService.setAuthToken(token);
        this.purchaseService.setAuthToken(token);
        this.licenseService.setAuthToken(token);
        this.customerService.setAuthToken(token);
        this.packageService.setAuthToken(token);
        this.agentNameTypeaheadService.setAuthToken(token);
        this.agentCodeTypeaheadService.setAuthToken(token);
        this.saveQuoteService.setAuthToken(token);
        this.emailQuoteService.setAuthToken(token);
        this.policyDocumentService.setAuthToken(token);
    }

    public injectAuthTokenFromLocalStorage(): void {
        if (window.localStorage) {
            let token: string = localStorage.getItem('idToken');
            if (typeof token === 'string' && token !== '') {
                this.injectAuthToken(token);
            }
        }
    }

    public injectFeatures(): void {
        let features: Features = new Features();
        if (window.global_airCareRatingIds) {
            features.airCareRatingIds = new Set<string>();
            window.global_airCareRatingIds.split(',').forEach((airCareRatingId: string) => {
                features.airCareRatingIds.add(airCareRatingId);
            });
        }

        features.defaultFlightSegments = Number(window.global_default_flight_segments);
        features.consumerDomain = window.global_consumer_domain;

        this.featureService.setFeatures(features);

        // Initialize feature toggle from URL parameter and store in sessionStorage
        this.featureService.isEventIdAsQuoteNumberEnabled();
    }

    public injectTypeaheadConfig(): void {
        let config: TypeaheadConfig = new TypeaheadConfig();

        // Store the function, not the result
        config.isCompatibilityMode = this.tramsService.isTramsSession;

        this.airlineTypeaheadService.config = config;
        this.countryTypeaheadService.config = config;
        this.carRentalTypeaheadService.config = config;
        this.cruiseTypeaheadService.config = config;
        this.tourTypeaheadService.config = config;
        this.hotelTypeaheadService.config = config;
        this.rentalCompTypeaheadService.config = config;
        this.agentNameTypeaheadService.config = config;
        this.agentCodeTypeaheadService.config = config;
    }

    constructor(
        private airlineTypeaheadService: BhtpAirlineTypeaheadService,
        private countryTypeaheadService: BhtpCountryTypeaheadService,
        private policyEditService: BhtpEditService,
        private carRentalTypeaheadService: BhtpCarRentalTypeaheadService,
        private cruiseTypeaheadService: BhtpCruiseTypeaheadService,
        private tourTypeaheadService: BhtpTourTypeaheadService,
        private hotelTypeaheadService: BhtpHotelTypeaheadService,
        private rentalCompTypeaheadService: BhtpRentalCompanyTypeaheadService,
        private agentNameTypeaheadService: AgentNameTypeaheadService,
        private agentCodeTypeaheadService: AgentCodeTypeaheadService,
        private stateService: BhtpStateService,
        private countryService: BhtpCountryService,
        private purchaseService: BhtpPurchaseService,
        private rulesService: BhtpRulesService,
        private eligibilityService: BhtpEligibilityService,
        private licenseService: BhtpLicenseService,
        private packageService: BhtpPackageService,
        private paymentService: PaymentService,
        private customerService: CustomerService,
        private featureService: FeatureService,
        private jurisdictionsService: BhtpJurisdictionsService,
        private tramsService: TramsService,
        private saveQuoteService: SaveQuoteService,
        private emailQuoteService: EmailQuoteService,
        private policyDocumentService: PolicyDocumentService) {
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/core-services/service-setup.service.ts